import { AxiosRequestConfig } from 'axios';
import { pigspinBackofficeApiInstance } from "../../utils/api/AxiosInstance";
import { fetchWithJSON } from "../../utils/api/fetch";
import { BaseResponse } from "../service.type";

export type ScopeItem = {
  uid: string;
  name: string;
  description: string | null;
}

export type PositionScopeItem = {
  uid: string;
  name: string;
  description: string | null;
  scope_uids: string[];
}

export type UserScopeDetail = {
  uid: string;
  name: string | null;
  email: string;
  personal_number: string;
  role: string;
  position_uid: string;
  position_scope_uids: string[];
  scope_uids: string[];
}

type ScopeListResponse = BaseResponse & {
  data: { total: number; results: ScopeItem[] };
}

type PositionScopeListResponse = BaseResponse & {
  data: { results: PositionScopeItem[] };
}

type UserScopeDetailResponse = BaseResponse & {
  data: UserScopeDetail;
}

export const getAllScopes = async (token: string = ''): Promise<ScopeItem[] | null> => {
  try {
    const requestConfig: AxiosRequestConfig = {
      url: 'v1/scope-management/scopes',
      method: 'GET',
      headers: { token },
    };
    const result = await fetchWithJSON<ScopeListResponse>(pigspinBackofficeApiInstance, requestConfig);
    return result.data?.results || [];
  } catch (error) {
    return null;
  }
};

export const createScope = async (
  name: string,
  description: string,
  token: string = '',
): Promise<boolean> => {
  try {
    const requestConfig: AxiosRequestConfig = {
      url: 'v1/scope-management/scopes',
      method: 'POST',
      data: { name, description: description || null },
      headers: { token },
    };
    const result = await fetchWithJSON<BaseResponse>(pigspinBackofficeApiInstance, requestConfig);
    return result.status === 'SUCCESS';
  } catch (error) {
    return false;
  }
};

export const getPositionScopes = async (token: string = ''): Promise<PositionScopeItem[] | null> => {
  try {
    const requestConfig: AxiosRequestConfig = {
      url: 'v1/scope-management/positions',
      method: 'GET',
      headers: { token },
    };
    const result = await fetchWithJSON<PositionScopeListResponse>(pigspinBackofficeApiInstance, requestConfig);
    return result.data?.results || [];
  } catch (error) {
    return null;
  }
};

export const updatePositionScopes = async (
  positionUid: string,
  scopeUids: string[],
  token: string = '',
): Promise<boolean> => {
  try {
    const requestConfig: AxiosRequestConfig = {
      url: `v1/scope-management/positions/${positionUid}`,
      method: 'PUT',
      data: { scope_uids: scopeUids },
      headers: { token },
    };
    const result = await fetchWithJSON<BaseResponse>(pigspinBackofficeApiInstance, requestConfig);
    return result.status === 'SUCCESS';
  } catch (error) {
    return false;
  }
};

export const getUserScopes = async (
  userUid: string,
  token: string = '',
): Promise<UserScopeDetail | null> => {
  try {
    const requestConfig: AxiosRequestConfig = {
      url: `v1/scope-management/users/${userUid}`,
      method: 'GET',
      headers: { token },
    };
    const result = await fetchWithJSON<UserScopeDetailResponse>(pigspinBackofficeApiInstance, requestConfig);
    return result.data;
  } catch (error) {
    return null;
  }
};

export const updateUserScopes = async (
  userUid: string,
  scopeUids: string[],
  token: string = '',
): Promise<boolean> => {
  try {
    const requestConfig: AxiosRequestConfig = {
      url: `v1/scope-management/users/${userUid}`,
      method: 'PUT',
      data: { scope_uids: scopeUids },
      headers: { token },
    };
    const result = await fetchWithJSON<BaseResponse>(pigspinBackofficeApiInstance, requestConfig);
    return result.status === 'SUCCESS';
  } catch (error) {
    return false;
  }
};
